import { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from 'context/AuthContext.jsx';
import { useTheme } from 'context/ThemeContext.jsx';
import { useDashData } from 'context/DashDataContext.jsx';
import { useDialogs } from 'context/ConfirmContext.jsx';
import { Button } from 'components/common';
import UpdatesBanner from 'components/UpdatesBanner.jsx';
import WeekGate from 'components/WeekGate.jsx';
import NotificationBell from 'components/NotificationBell.jsx';
import { NAV_ITEMS } from './navigation.js';

/**
 * The classic shell — topbar, tab bar, centred content column.
 *
 * Serves BOTH v1 and v2. They are the same DOM; v2 is entirely a
 * [data-theme="v2"] override layer in the stylesheet, so there is no branching
 * here and there should never be any. The only structural difference between the
 * two themes anywhere in the app is the Overview view, which is handled in the
 * route table, not here.
 */
export default function V1V2Shell() {
  const { name, logout, token, isAdmin } = useAuth();
  const { theme, cycleTheme, bootEnabled, toggleBoot, startBoot } = useTheme();
  const { weekLabel, weekOffset, changeWeek, loading } = useDashData();
  const { alert, prompt } = useDialogs();
  const [copied, setCopied] = useState(false);

  /*
   * The token is what the CEO Dashboard authenticates with, so this is a
   * deliberate copy-to-clipboard of a credential. The clipboard API needs a
   * secure context and can be refused outright; the original fell back to a
   * native prompt() so the value was still recoverable. Same fallback here,
   * through the themed dialog.
   *
   * `token` / `mid_token` are what this value was stored under before af_token,
   * and the original checked all three HERE and nowhere else — the rest of it
   * read af_token alone. Kept at the same narrow scope deliberately: treating an
   * older key as proof of a session would sign someone in without the af_role
   * that decides what they are allowed to see.
   */
  const legacyToken = () => {
    try {
      return localStorage.getItem('token') || localStorage.getItem('mid_token') || '';
    } catch { return ''; }
  };

  const copyToken = async () => {
    const value = token || legacyToken();
    if (!value) { await alert('No token found. Please sign in first.'); return; }
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      await prompt('Copy this token:', { title: 'API token', defaultValue: value, confirmLabel: 'Done' });
    }
  };

  return (
    <div id="dashboard-page">
      <div className="topbar">
        <h1><span className="status-dot" />Affiliate Dashboard</h1>
        <div className="topbar-right">
          <div className="week-nav">
            <Button variant="bare" onClick={() => changeWeek(-1)} aria-label="Previous week">◀</Button>
            <span className="week-label">{loading ? 'Loading…' : (weekLabel || '—')}</span>
            <Button variant="bare" onClick={() => changeWeek(1)} disabled={weekOffset >= 0} aria-label="Next week">▶</Button>
          </div>
          <Button
            variant="toggle"
            onClick={cycleTheme}
            title="Switch between the V1, V2 and V3 interfaces"
          >
            THEME: {theme.toUpperCase()}
          </Button>
          <Button
            variant="toggle"
            active={bootEnabled}
            onClick={toggleBoot}
            aria-pressed={bootEnabled}
            title="Toggle the boot sequence (V2/V3 only)"
          >
            BOOT: {bootEnabled ? 'ON' : 'OFF'}
          </Button>
          <Button
            variant="bare"
            id="hax-replay"
            onClick={startBoot}
            disabled={!bootEnabled}
            title={bootEnabled
              ? 'Replay the boot sequence'
              : 'Turn BOOT on to replay the sequence'}
          >
            ↻ REPLAY BOOT
          </Button>
          <Button
            variant="token"
            active={copied}
            onClick={copyToken}
            title="Copy your API token for the CEO Dashboard"
          >
            {copied ? '✅ Copied!' : '🔑 Copy Token'}
          </Button>
          {/* Beside the user's name rather than out among the theme and boot
              toggles: it belongs to "you", not to the display settings, and it
              is the one control here whose appearance changes on its own. */}
          <NotificationBell variant="classic" />
          {name && <span className="topbar__user">{name}</span>}
          <Button variant="logout" onClick={logout}>Sign out</Button>
        </div>
      </div>

      <div className="tab-bar">
        {NAV_ITEMS.filter(item => !item.adminOnly || isAdmin).map(item => (
          <NavLink
            key={item.path}
            to={item.path}
            title={item.title}
            className={({ isActive }) => `tab${isActive ? ' active' : ''}`}
          >
            {item.tab}
          </NavLink>
        ))}
      </div>

      <div className="main">
        <UpdatesBanner />
        <div id="tab-content"><WeekGate><Outlet /></WeekGate></div>
      </div>
    </div>
  );
}
